/*
 * Number formatting for the Potential Energy Savings card, by the element it feeds:
 *
 */
const GWH_THRESHOLD = 1000000;
const MWH_THRESHOLD = 1000; // Stat
const MWH_THRESHOLD_SERIES = 10000; // Chart bigger threshold because it would reduce the bar very small on the chart.

export type EnergyUnit = 'kWh' | 'MWh' | 'GWh';

export interface ScaledEnergy {
  value: number;
  unit: EnergyUnit;
  /** What the raw figure was divided by -- so a related value can match this scale. */
  divisor: number;
}

/**
 * Scale one kWh figure to the largest unit that keeps it readable. Uses the single-
 * figure cutoff (1000) -- for the chart's axis use scaleEnergyAxis instead.
 *
 * @param {number} kwh raw kilowatt-hours
 * @returns {ScaledEnergy} the scaled value and the unit it is now in
 */
export function scaleEnergy (kwh: number): ScaledEnergy {
  const safe = Number.isFinite(kwh) ? kwh : 0;
  const magnitude = Math.abs(safe);

  if (magnitude >= GWH_THRESHOLD) return { value: safe / GWH_THRESHOLD, unit: 'GWh', divisor: GWH_THRESHOLD };
  if (magnitude >= MWH_THRESHOLD) return { value: safe / MWH_THRESHOLD, unit: 'MWh', divisor: MWH_THRESHOLD };

  return { value: safe, unit: 'kWh', divisor: 1 };
}

/**
 * The chip's pair: both figures on one shared unit, chosen from the larger, so it
 * never reads "83 kWh / 97 MWh".
 *
 * @param {number} a first figure (the wasted portion)
 * @param {number} b second figure (the total)
 * @returns {{ a: number, b: number, unit: EnergyUnit }} both values in a shared unit
 */
export function scaleEnergyPair (a: number, b: number): { a: number, b: number, unit: EnergyUnit } {
  const { unit, divisor } = scaleEnergy(Math.max(Math.abs(a) || 0, Math.abs(b) || 0));

  return { a: (a || 0) / divisor, b: (b || 0) / divisor, unit };
}

/**
 * Format a currency amount for display, whole-dollar (the headline has no cents).
 *
 * @param {number} amount the value
 * @param {string} currency currency code from the API, e.g. "USD"
 * @param {string} locale language tag from the user's language preference, e.g. "en-US"
 * @returns {string} e.g. "$11,293"
 */
export function formatCurrency (amount: number, currency: string, locale: string): string {
  const safe = Number.isFinite(amount) ? amount : 0;

  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
      maximumFractionDigits: 0,
    }).format(safe);
  } catch {
    /* An unknown currency code throws; fall back rather than blanking the headline. */
    return new Intl.NumberFormat(locale, { maximumFractionDigits: 0 }).format(safe);
  }
}

/**
 * The chart's kWh axis: one unit for the whole series, from its largest value, so the
 * axis, its top-tick label and the bar tooltips all read the same. Shaped for
 * ChartSeries.scale; the chart divides the data through, nothing is rounded here.
 * Switches to MWh at 10000, not 1000 -- see the threshold note above.
 *
 * @param {number} max the series' largest absolute value, in kWh
 * @returns {{ divisor: number, unit: EnergyUnit }} the divisor and unit to display
 */
export function scaleEnergyAxis (max: number): { divisor: number, unit: EnergyUnit } {
  /* Strictly greater-than, as v1's getKWHTitle is, so the boundaries line up. */
  const magnitude = Math.abs(Number.isFinite(max) ? max : 0);

  if (magnitude > GWH_THRESHOLD) return { divisor: GWH_THRESHOLD, unit: 'GWh' };
  if (magnitude > MWH_THRESHOLD_SERIES) return { divisor: MWH_THRESHOLD, unit: 'MWh' };

  return { divisor: 1, unit: 'kWh' };
}

/**
 * Format a $/kWh rate, which needs the cents formatCurrency deliberately rounds away --
 * a 0.08 rate would otherwise read as "$0". Trailing zeros are kept so a rate always
 * reads as money, and up to 3 decimals are allowed since rates are quoted that finely.
 *
 * @param {number} rate the per-kWh rate
 * @param {string} currency currency code, e.g. "USD"
 * @param {string} locale language tag, e.g. "en-US"
 * @returns {string} the formatted rate
 */
export function formatRate (rate: number, currency: string, locale: string): string {
  const safe = Number.isFinite(rate) ? rate : 0;
  const opts = { minimumFractionDigits: 2, maximumFractionDigits: 3 };

  try {
    return new Intl.NumberFormat(locale, { style: 'currency', currency, ...opts }).format(safe);
  } catch {
    return new Intl.NumberFormat(locale, opts).format(safe);
  }
}

/**
 * Format a scaled energy value -- whole numbers, since the chip is a rough measure.
 *
 * @param {number} value already scaled by scaleEnergy/scaleEnergyPair
 * @param {string} locale language tag, e.g. "en-US"
 * @returns {string} the formatted number, without its unit
 */
export function formatEnergy (value: number, locale: string): string {
  const safe = Number.isFinite(value) ? value : 0;

  return new Intl.NumberFormat(locale, { maximumFractionDigits: 0 }).format(safe);
}
